import { type FlexProps, Box, Flex, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';

export type InfoHighlightPillSize = 'sm' | 'md' | 'lg' | 'banner';
export type InfoHighlightPillVariant = 'solid' | 'gradient' | 'success';

export type InfoHighlightPillProps = {
  children: ReactNode;
  /** Optional leading icon element (MUI / custom). Preferred over `iconSrc`. */
  icon?: ReactNode;
  /** Optional leading icon image path (e.g. scale SVG). */
  iconSrc?: string;
  size?: InfoHighlightPillSize;
  /**
   * `solid` — Figma checkout / pending stock pill (`#FFF9E1`).
   * `gradient` — Figma PDP / Timbangan banner fade (`#FFF9E1` → white).
   * `success` — Figma confirmed stock pill (`#F1FFF1`).
   */
  variant?: InfoHighlightPillVariant;
  /** Override border radius (defaults per size). */
  borderRadius?: FlexProps['borderRadius'];
  align?: 'center' | 'flex-start';
  whiteSpace?: FlexProps['whiteSpace'];
  'data-testid'?: string;
};

const SIZE_STYLES: Record<
  InfoHighlightPillSize,
  {
    gap: number;
    px: number;
    py: number;
    minH?: string;
    iconBox: string;
    fontSize: string;
    borderRadius: string;
  }
> = {
  /** Checkout product-row status (Figma Berat estimasi). */
  sm: {
    gap: 1,
    px: 2,
    py: 0.5,
    iconBox: '16px',
    fontSize: '12px',
    borderRadius: '24px',
  },
  /** Compact banners (PDP mobile / drawer). */
  md: {
    gap: 1,
    px: 5,
    py: 2,
    iconBox: '20px',
    fontSize: '12px',
    borderRadius: '24px',
  },
  /** Desktop PDP banners. */
  lg: {
    gap: 2,
    px: 6,
    py: 2,
    minH: '36px',
    iconBox: '20px',
    fontSize: '14px',
    borderRadius: '24px',
  },
  /** Checkout payment Timbangan notice (Figma 21683:277461). */
  banner: {
    gap: 2,
    px: 4,
    py: 2,
    iconBox: '20px',
    fontSize: '12px',
    borderRadius: '8px',
  },
};

const VARIANT_BG: Record<InfoHighlightPillVariant, string> = {
  solid: '#FFF9E1',
  gradient: 'linear-gradient(90deg, #FFF9E1 37%, #FFFFFF 100%)',
  success: '#F1FFF1',
};

/**
 * Reusable info highlight pill (Figma estimated-weight / notice chips).
 * Use for product-detail banners, checkout status, payment notices, etc.
 */
export function InfoHighlightPill({
  children,
  icon,
  iconSrc,
  size = 'sm',
  variant = 'solid',
  borderRadius,
  align = 'center',
  whiteSpace,
  'data-testid': dataTestId,
}: InfoHighlightPillProps) {
  const styles = SIZE_STYLES[size];

  const leadingIcon = (() => {
    if (icon) {
      return (
        <Box
          flexShrink={0}
          w={styles.iconBox}
          h={styles.iconBox}
          minW={styles.iconBox}
          minH={styles.iconBox}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          mt={align === 'flex-start' ? '2px' : undefined}
          color="#373F41"
          sx={{
            '& > svg': {
              width: styles.iconBox,
              height: styles.iconBox,
            },
          }}
        >
          {icon}
        </Box>
      );
    }

    if (iconSrc) {
      return (
        <Box
          as="img"
          src={iconSrc}
          alt=""
          aria-hidden
          w={styles.iconBox}
          h={styles.iconBox}
          minW={styles.iconBox}
          minH={styles.iconBox}
          flexShrink={0}
          mt={align === 'flex-start' ? '2px' : undefined}
        />
      );
    }

    return null;
  })();

  return (
    <Flex
      align={align}
      gap={styles.gap}
      px={styles.px}
      py={styles.py}
      minH={styles.minH}
      w="auto"
      maxW="full"
      borderRadius={borderRadius ?? styles.borderRadius}
      bg={VARIANT_BG[variant]}
      data-testid={dataTestId}
    >
      {leadingIcon}
      <Text
        fontSize={styles.fontSize}
        color="#373F41"
        lineHeight="short"
        whiteSpace={whiteSpace}
      >
        {children}
      </Text>
    </Flex>
  );
}
